import React, { useState, useEffect } from 'react';
import { groupBy } from 'lodash';

import CurrentUser from 'auth/CurrentUser';
import EventTracking from 'lib/EventTracking';
import { coachingHomeEventPrefix } from 'coaching/components/CoachingHomeView/CoachingHomeView';

import BoxBody from 'components/BoxBody';
import { Button as ButtonNew } from 'components/Button';
import Button from 'components/ButtonDeprecated';
import CancelRecurringSession from 'coaching/components/CoachingHomeView/CancelRecurringSession';
import {
  CoachSidebarHeader,
  CoachingSessionScheduleRow,
  getScheduleDescriptionForSidebar,
} from 'coaching/components/CoachingHomeView/ManageRecurringSessionsRows';
import ConfirmCancelledRecurringSessionOrInstance from 'coaching/components/CoachingHomeView/ConfirmCancelledRecurringSessionOrInstance';
import Divider from 'components/Divider';
import Icon from 'components/Icon';
import ManageRecurringSession from 'coaching/components/CoachingHomeView/ManageRecurringSession';
import MediaObject from 'components/MediaObject';
import Row from 'components/Row';
import SidebarFooter from 'components/SidebarFooter';
import Text from 'components/Text';
import TextWithIcon from 'components/TextWithIcon';
import ToastActions from 'actions/ToastActions';
import SidebarHeader, { Props as SidebarHeaderProps } from 'coaching/components/SidebarHeader';

import { TimeslotFrequencyInput } from 'globalTypes';
import { wouldBeLateCancellation } from 'coaching/components/CoachingHomeView/CoachingSessionInstanceRowComponents';
import { deleteCoachingSession } from 'coaching/actions/CoachingActions';
import GET_COACHING_HOME_DASHBOARD_QUERY from 'coaching/queries/GetCoachingHomeDashboard.gql';
import {
  CoachingSessionSchedule,
  CoachingSessionInstance,
} from 'coaching/components/CoachingHomeView/CoachingSessionsBySubjectContainer';

import 'coaching/components/styles/ManageRecurringSessionSidebar.scss';

type CancellationSteps =
  | 'select-schedule'
  | 'edit-schedule'
  | 'cancel-recurring-session'
  | 'confirmation';

export type Props = {
  participantUuid: string;
  recurringCoachingSessionSchedules: CoachingSessionSchedule[];
  upcomingInstances?: CoachingSessionInstance[];
  subjectName: string;
  onClose: () => void;
  onClickBookSession: () => void;
};

export default function ManageRecurringSessionSidebar({
  participantUuid,
  recurringCoachingSessionSchedules,
  upcomingInstances,
  subjectName,
  onClose,
  onClickBookSession,
}: Props) {
  const currentUser = CurrentUser.getUser();
  const hasMultipleRecurringSessionsFeature = Boolean(
    currentUser && currentUser.hasFeature('add_recurring_sessions.web.dec-21')
  );

  const hasMultipleSchedules = recurringCoachingSessionSchedules.length > 1;
  const defaultStep =
    hasMultipleSchedules || hasMultipleRecurringSessionsFeature
      ? 'select-schedule'
      : 'edit-schedule';
  const defaultCoachingSessionSchedule = hasMultipleSchedules
    ? undefined
    : recurringCoachingSessionSchedules[0];

  const [step, setStep] = useState<CancellationSteps>(defaultStep);
  const [submitting, setSubmitting] = useState(false);
  const [cancellationReason, setCancellationReason] = useState<string>();
  const [cancellationDetails, setCancellationDetails] = useState<string>();
  const [coachingSessionSchedule, setCoachingSessionSchedule] = useState<
    CoachingSessionSchedule | undefined
  >(defaultCoachingSessionSchedule);

  const coachName = coachingSessionSchedule?.coach.name;
  const nextInstanceOfSelectedSchedule = upcomingInstances?.find(
    instance => instance.coachingSessionScheduleUuid === coachingSessionSchedule?.uuid
  );
  const isLateCancellation =
    nextInstanceOfSelectedSchedule &&
    wouldBeLateCancellation(nextInstanceOfSelectedSchedule.startDateTime);

  function getEventComponentName(): 'manage_plan_sidebar' | 'cancel_plan_sidebar' {
    switch (step) {
      case 'edit-schedule':
      case 'select-schedule':
        return 'manage_plan_sidebar';

      case 'cancel-recurring-session':
      case 'confirmation':
        return 'cancel_plan_sidebar';
    }
  }

  const eventPrefix = `${coachingHomeEventPrefix}.${getEventComponentName()}`;
  function trackKey(key: string) {
    EventTracking.trackKey(`${eventPrefix}.${key}`);
  }

  useEffect(() => {
    trackKey('view');
  }, []);

  function resetCancellationExplanation() {
    setCancellationReason(undefined);
    setCancellationDetails(undefined);
  }

  useEffect(() => {
    // reset state if we change the participant/subject
    setStep(defaultStep);
    resetCancellationExplanation();
    setCoachingSessionSchedule(defaultCoachingSessionSchedule);
  }, [participantUuid, subjectName]);

  // reset the cancellation explanation when the user
  // goes back to the edit schedule step
  useEffect(() => {
    resetCancellationExplanation();
  }, [step === 'edit-schedule']);

  const onCancellationSuccess = () => {
    setStep('confirmation');
  };

  async function handleSubmit() {
    if (cancellationReason && coachingSessionSchedule) {
      setSubmitting(true);
      await deleteCoachingSession(
        coachingSessionSchedule.uuid,
        cancellationReason,
        undefined,
        undefined,
        [
          {
            query: GET_COACHING_HOME_DASHBOARD_QUERY,
            variables: {},
          },
        ],
        onCancellationSuccess
      );
      setSubmitting(false);
    } else {
      ToastActions.showErrorToast(
        'Oops! Looks like you tried to delete a session without specifying a cancellation reason. Please try again.'
      );
    }
  }

  function getScheduleFrequency() {
    const frequency = coachingSessionSchedule?.description;
    switch (frequency) {
      case 'Weekly':
        return TimeslotFrequencyInput.weekly;
      case 'Every other week':
        return TimeslotFrequencyInput.everyOtherWeek;
    }
  }

  function getCancelSessionsCTA() {
    if (hasMultipleRecurringSessionsFeature) {
      const frequency = coachingSessionSchedule?.description;
      switch (frequency) {
        case 'Weekly':
          return 'Cancel weekly sessions';
        case 'Every other week':
          return 'Cancel biweekly sessions';
      }
    }

    return 'Cancel recurring session';
  }

  const stepToHeaderPropsMap: Record<CancellationSteps, SidebarHeaderProps> = {
    'select-schedule': {
      title: 'Edit schedule',
    },
    'edit-schedule': {
      title: 'Edit schedule',
      onBack: hasMultipleSchedules ? () => setStep('select-schedule') : undefined,
    },
    'cancel-recurring-session': {
      title: hasMultipleRecurringSessionsFeature ? 'Cancel sessions' : 'Cancel recurring session',
      onBack: () =>
        setStep(hasMultipleRecurringSessionsFeature ? 'select-schedule' : 'edit-schedule'),
    },
    confirmation: {
      title: hasMultipleRecurringSessionsFeature ? 'Cancel sessions' : 'Cancel recurring session',
    },
  };

  const stepToSubHeaderMap: Record<CancellationSteps, React.ReactNode> = {
    'select-schedule': null,
    'edit-schedule': <CoachSidebarHeader subjectName={subjectName} coachName={coachName} />,
    'cancel-recurring-session': (
      <CoachSidebarHeader
        data-test="cancel-sessions-coachSidebarHeader"
        subjectName={subjectName}
        coachName={coachName}
        hideBottomDivider
      />
    ),
    confirmation: null,
  };

  // The key here comes back as a concatenated string - "<coachUuid>,<coachName>"
  const schedulesGroupedByCoach: Record<
    string,
    CoachingSessionSchedule[]
  > = groupBy(recurringCoachingSessionSchedules, schedule => [
    schedule.coach.uuid,
    schedule.coach.name,
  ]);

  const stepToComponentMap: Record<CancellationSteps, React.ReactElement> = {
    'select-schedule': (
      <div data-test="select-schedule-step">
        {Object.entries(schedulesGroupedByCoach).map(([coachUuidNameKey, schedules]) => {
          return (
            <div
              key={coachUuidNameKey}
              className="ManageRecurringSessionSidebar-sessionScheduleRow">
              <CoachSidebarHeader
                coachName={coachUuidNameKey.split(',')[1]}
                subjectName={subjectName}
                hideBottomDivider
              />
              {schedules.map(schedule => {
                return (
                  <div data-test={'coaching-session-schedule-row'} key={schedule.uuid}>
                    <CoachingSessionScheduleRow
                      data-test={`coaching-session-schedule-row-${schedule.uuid}`}
                      schedule={schedule}
                      onClick={
                        hasMultipleRecurringSessionsFeature
                          ? () => {
                              setCoachingSessionSchedule(schedule);
                              setStep('cancel-recurring-session');
                            }
                          : () => {
                              setCoachingSessionSchedule(schedule);
                              setStep('edit-schedule');
                            }
                      }
                    />
                  </div>
                );
              })}
            </div>
          );
        })}
        {hasMultipleRecurringSessionsFeature && (
          <>
            <Row>
              <BoxBody full margin="small">
                <Divider size="flush" />
              </BoxBody>
            </Row>
            <BoxBody full margin="small">
              <ButtonNew data-test="add-sessions-button" full onClick={onClickBookSession}>
                <TextWithIcon inline icon="+">
                  Add sessions
                </TextWithIcon>
              </ButtonNew>
            </BoxBody>
          </>
        )}
      </div>
    ),
    'edit-schedule': (
      <ManageRecurringSession
        data-test="edit-schedule-step"
        eventPrefix={eventPrefix}
        onCancelPlan={() => setStep('cancel-recurring-session')}
      />
    ),
    'cancel-recurring-session': (
      <>
        <CancelRecurringSession
          data-test="cancel-recurring-session-step"
          eventPrefix={eventPrefix}
          coachName={coachName}
          wouldBeLateCancellation={isLateCancellation}
          onSelectCancellationReason={setCancellationReason}
          onChangeCancellationDetails={setCancellationDetails}
          scheduleDescription={
            coachingSessionSchedule && getScheduleDescriptionForSidebar(coachingSessionSchedule)
          }
        />
        <SidebarFooter>
          <BoxBody size="small">
            <Button
              full
              color="apple"
              data-test="cancel-recurring-schedule"
              eventKey={`${eventPrefix}.cancel_plan.click`}
              eventProperties={{
                data: JSON.stringify({
                  cancellation_details: cancellationDetails,
                  participant_uuid: participantUuid,
                  coaching_session_uuid: coachingSessionSchedule?.uuid,
                }),
              }}
              disabled={!cancellationReason || submitting}
              loading={submitting}
              onClick={handleSubmit}>
              {getCancelSessionsCTA()}
            </Button>
          </BoxBody>
        </SidebarFooter>
      </>
    ),
    confirmation: (
      <ConfirmCancelledRecurringSessionOrInstance
        frequency={getScheduleFrequency()}
        scheduleStartDateTime={coachingSessionSchedule?.startDateTime}
        data-test="confirm-cancelled-recurring-schedule-step"
        eventPrefix={eventPrefix}
        onClickBookSession={onClickBookSession}
      />
    ),
  };

  return (
    <>
      <SidebarHeader
        closeEvent={`${eventPrefix}.close.click`}
        onClose={onClose}
        {...stepToHeaderPropsMap[step]}
      />
      {stepToSubHeaderMap[step]}
      {stepToComponentMap[step]}
    </>
  );
}
